import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Check, X, Sparkles, Rocket, Building2, Crown, 
  ArrowRight, Zap, Shield, Users, Globe, Star,
  TrendingUp, MessageSquare, BarChart3, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function Pricing() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [foundingCount, setFoundingCount] = useState({ claimed: 0, total_slots: 500, remaining: 500 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claimingSpot, setClaimingSpot] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showAlreadyFounderDialog, setShowAlreadyFounderDialog] = useState(false);
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);

  useEffect(() => {
    fetchPricingData();
    checkUserStatus();
    
    // Auto-refresh founding count every 30 seconds
    const interval = setInterval(fetchFoundingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPricingData = async () => {
    try {
      setError(null);
      const [tiersRes, countRes] = await Promise.all([
        axios.get(`${API}/api/pricing/tiers`),
        axios.get(`${API}/api/pricing/founding/count`)
      ]);
      setTiers(tiersRes.data.tiers || []);
      setFoundingCount(countRes.data);
    } catch (err) {
      console.error('Failed to fetch pricing data');
      setError('Unable to load pricing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFoundingCount = async () => {
    try {
      const res = await axios.get(`${API}/api/pricing/founding/count`);
      setFoundingCount(res.data);
    } catch (error) {
      // Silent fail for background refresh
    }
  };

  const checkUserStatus = async () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    if (token) {
      try {
        const res = await axios.get(`${API}/api/pricing/user-plan`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserPlan(res.data);
      } catch (error) {
        // User not logged in or token expired
      }
    }
  };

  const handleClaimFoundingSpot = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      // Redirect to signup
      navigate('/auth?mode=register&founding=true');
      return;
    }

    // Check if already founding member
    if (userPlan?.founding_member) {
      setShowAlreadyFounderDialog(true);
      return;
    }

    setClaimingSpot(true);
    try {
      const res = await axios.post(`${API}/api/pricing/founding/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        if (res.data.already_claimed) {
          setShowAlreadyFounderDialog(true);
        } else {
          setShowSuccessDialog(true);
          fetchFoundingCount();
          checkUserStatus();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim spot');
    } finally {
      setClaimingSpot(false);
    }
  };

  const handleCTAClick = (tier) => {
    if (tier.id === 'starter') {
      navigate('/auth?mode=register');
    } else if (tier.id === 'pro') {
      handleClaimFoundingSpot();
    } else if (tier.id === 'enterprise') {
      window.location.href = 'mailto:support@globalvyapar.co.in?subject=Enterprise Plan Inquiry';
    }
  };

  const progressPercentage = (foundingCount.claimed / foundingCount.total_slots) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Founding Exporter Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-3 px-4">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
          <Rocket className="h-5 w-5 animate-pulse" />
          <span className="font-semibold">
            First 500 exporters get Pro FREE for 12 months
          </span>
          <span className="hidden sm:inline">•</span>
          <span className="font-bold text-lg">
            {foundingCount.claimed}/{foundingCount.total_slots} claimed
          </span>
          <div className="w-32 h-2 bg-white/30 rounded-full overflow-hidden ml-2">
            <div 
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="container mx-auto px-4 pt-16 pb-12 text-center">
        <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
          <Sparkles className="h-3 w-3 mr-1" />
          Founding Exporter Program
        </Badge>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto">
          Start free, upgrade when you're ready. Join {foundingCount.total_exporters > 0 ? `${foundingCount.total_exporters}+` : '100+'} verified exporters already on the platform.
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="container mx-auto px-4 pb-8">
          <div className="max-w-md mx-auto bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
            <p className="text-red-400 mb-3">{error}</p>
            <Button 
              onClick={fetchPricingData}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              Try Again
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="container mx-auto px-4 pb-20">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-slate-800/50 border-slate-700 animate-pulse">
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto mb-4 p-3 rounded-xl w-12 h-12 bg-slate-700" />
                  <div className="h-6 bg-slate-700 rounded w-24 mx-auto" />
                  <div className="h-10 bg-slate-700 rounded w-32 mx-auto mt-4" />
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((j) => (
                      <div key={j} className="h-4 bg-slate-700 rounded" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      {!loading && !error && (
      <div className="container mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {tiers.map((tier, index) => (
            <Card 
              key={tier.id}
              className={`relative bg-slate-800/50 border-slate-700 backdrop-blur-sm transition-all duration-300 hover:scale-105 ${
                tier.is_popular 
                  ? 'border-emerald-500 ring-2 ring-emerald-500/20 scale-105' 
                  : 'hover:border-slate-600'
              }`}
            >
              {tier.is_popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-emerald-500 text-white px-4 py-1">
                    <Star className="h-3 w-3 mr-1 fill-current" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`mx-auto mb-4 p-3 rounded-xl w-fit ${
                  tier.id === 'starter' ? 'bg-slate-700' :
                  tier.id === 'pro' ? 'bg-emerald-500/20' :
                  'bg-purple-500/20'
                }`}>
                  {tier.id === 'starter' && <Users className="h-6 w-6 text-slate-400" />}
                  {tier.id === 'pro' && <Crown className="h-6 w-6 text-emerald-400" />}
                  {tier.id === 'enterprise' && <Building2 className="h-6 w-6 text-purple-400" />}
                </div>
                
                <CardTitle className="text-2xl text-white">{tier.name}</CardTitle>
                
                <div className="mt-4">
                  {tier.price_usd === 0 ? (
                    <div className="text-4xl font-bold text-white">Free</div>
                  ) : tier.price_usd === null ? (
                    <div className="text-3xl font-bold text-white">Custom</div>
                  ) : (
                    <div>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-white">${tier.price_usd}</span>
                        <span className="text-slate-400">/mo</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {tier.founding_offer && (
                  <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 text-sm font-medium flex items-center justify-center gap-1">
                      <Zap className="h-4 w-4" />
                      {tier.founding_offer}
                    </p>
                  </div>
                )}
              </CardHeader>
              
              <CardContent className="pt-4">
                <ul className="space-y-3 mb-6">
                  {tier.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-slate-600 shrink-0 mt-0.5" />
                      )}
                      <span className={feature.included ? 'text-slate-300' : 'text-slate-500'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => handleCTAClick(tier)}
                  disabled={claimingSpot && tier.id === 'pro'}
                  className={`w-full ${
                    tier.id === 'pro' 
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                      : tier.id === 'enterprise'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                  variant={tier.cta_variant === 'outline' ? 'outline' : 'default'}
                  data-testid={`pricing-cta-${tier.id}`}
                >
                  {claimingSpot && tier.id === 'pro' ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Claiming...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {tier.cta_text}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <div className="flex flex-wrap items-center justify-center gap-6 text-slate-400">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-400" />
              <span>GST Verified</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-400" />
              <span>IEC Verified</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-400" />
              <span>{foundingCount.total_exporters > 0 ? `${foundingCount.total_exporters}+` : '100+'} Verified Exporters</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-400" />
              <span>Razorpay Secure</span>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="font-semibold text-white mb-2">What is the Founding Exporter program?</h3>
              <p className="text-slate-400">The first 500 exporters who sign up get our Pro plan completely free for 12 months. This includes all premium features like AI Buyer Radar, unlimited listings, and priority support.</p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="font-semibold text-white mb-2">What happens after 12 months?</h3>
              <p className="text-slate-400">You'll have the option to continue with Pro at the regular price or downgrade to Starter. We'll notify you 30 days before your founding period ends.</p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="font-semibold text-white mb-2">Can I upgrade from Starter to Pro later?</h3>
              <p className="text-slate-400">Yes! You can upgrade anytime. If founding spots are still available, you'll get the free 12-month benefit. Otherwise, regular pricing applies.</p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="font-semibold text-white mb-2">Is there a contract or commitment?</h3>
              <p className="text-slate-400">No contracts! The Starter plan is free forever, and Pro is month-to-month. Cancel anytime with no penalties.</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-emerald-600/20 via-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 rounded-2xl p-8 max-w-2xl mx-auto">
            <Rocket className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">
              Ready to grow your export business?
            </h2>
            <p className="text-slate-400 mb-6">
              Join {foundingCount.claimed} exporters who've already claimed their founding spot
            </p>
            <Button 
              size="lg"
              onClick={handleClaimFoundingSpot}
              disabled={claimingSpot}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
              data-testid="claim-founding-cta"
            >
              {claimingSpot ? 'Claiming...' : 'Claim Your Founding Spot'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-sm text-slate-500 mt-4">
              Only {foundingCount.remaining} spots remaining
            </p>
          </div>
        </div>
      </div>
      )}

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <div className="mx-auto mb-4 p-4 bg-emerald-500/20 rounded-full w-fit">
              <Crown className="h-8 w-8 text-emerald-400" />
            </div>
            <DialogTitle className="text-2xl text-center">
              Welcome, Founding Exporter! 🎉
            </DialogTitle>
            <DialogDescription className="text-center text-slate-400 space-y-4 pt-4">
              <p>
                Congratulations! You're now a <strong className="text-emerald-400">Founding Exporter</strong> with Pro plan unlocked FREE for 12 months.
              </p>
              <div className="bg-slate-800 rounded-lg p-4 text-left">
                <p className="font-semibold text-white mb-2">Your Pro benefits include:</p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> Unlimited product listings</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> AI Buyer Radar (find global buyers)</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> Verified Trust Badge</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> Priority listing rank</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> AI negotiation + follow-ups</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button 
              onClick={() => { setShowSuccessDialog(false); navigate('/dashboard'); }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Go to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Already Founder Dialog */}
      <Dialog open={showAlreadyFounderDialog} onOpenChange={setShowAlreadyFounderDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <div className="mx-auto mb-4 p-4 bg-amber-500/20 rounded-full w-fit">
              <Crown className="h-8 w-8 text-amber-400" />
            </div>
            <DialogTitle className="text-2xl text-center">
              You're Already a Founding Exporter! 🌟
            </DialogTitle>
            <DialogDescription className="text-center text-slate-400 pt-4">
              <p>
                Great news! You're already grandfathered into our Founding Exporter program with Pro plan free for 12 months.
              </p>
              <p className="mt-4">
                Your Pro features are fully unlocked. Keep growing your export business!
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button 
              onClick={() => { setShowAlreadyFounderDialog(false); navigate('/dashboard'); }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Go to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
